import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildPatchPayload, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import type { CustomQuestion } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Select,
  Spinner,
  Toggle,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';
import { useEventPicker } from '../shared/useEventPicker';

export function CustomQuestionsPage() {
  const { eventId, picker } = useEventPicker();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomQuestion | null>(null);
  const { hasModule, isLoading: pkgLoading } = useClientPackage();

  const questionsQuery = useQuery({
    queryKey: ['custom-questions', eventId],
    queryFn: () => api.customQuestions.list(eventId!),
    enabled: !!eventId,
  });

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: courtesy-only gate, see PromoCodesPage.tsx's
  // identical comment.
  if (!pkgLoading && !hasModule('customQuestions')) {
    return (
      <div>
        <PageHeader title="Custom questions" />
        <ModuleNotIncluded feature="Custom questions" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Custom questions"
        description="Collect extra information from buyers at checkout for this event."
        actions={
          <RequirePermission permission="custom_questions.manage">
            <Button
              disabled={!eventId}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New question
            </Button>
          </RequirePermission>
        }
      />
      <Card title="Event" className="mb-6">
        {picker}
      </Card>
      {!eventId ? (
        <p className="text-sm text-slate-500">Choose an event to manage its custom questions.</p>
      ) : questionsQuery.isLoading ? (
        <Spinner />
      ) : (
        <DataTable<CustomQuestion>
          columns={[
            { key: 'label', header: 'Question', render: (q) => q.label },
            { key: 'type', header: 'Type', render: (q) => <Badge>{q.type}</Badge> },
            { key: 'required', header: 'Required', render: (q) => (q.required ? 'Yes' : 'No') },
            { key: 'options', header: 'Options', render: (q) => q.options?.join(', ') ?? '-' },
          ]}
          rows={questionsQuery.data?.data ?? []}
          getRowKey={(q) => q.id}
          onRowClick={(q) => {
            setEditing(q);
            setModalOpen(true);
          }}
          emptyMessage="No custom questions for this event yet."
        />
      )}
      {eventId && (
        // REAL BUG FOUND AND FIXED (same root cause as EventDetailPage.tsx's TicketTypeFormModal,
        // found while live-verifying this round's clear-vs-untouched fix): always-mounted modal,
        // useState initializers only ran once at first mount - "Edit question" always opened
        // blank. `key` forces a real remount per edited record.
        <QuestionFormModal
          key={editing?.id ?? 'new'}
          eventId={eventId}
          question={editing}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function QuestionFormModal({
  eventId,
  question,
  open,
  onClose,
}: {
  eventId: string;
  question: CustomQuestion | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(question?.label ?? '');
  const [type, setType] = useState<CustomQuestion['type']>(question?.type ?? 'text');
  const [required, setRequired] = useState(question?.required ?? false);
  const [options, setOptions] = useState(question?.options?.join(', ') ?? '');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const parsedOptions = options
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean);
  const optionsError =
    type === 'select' && parsedOptions.length < 2 ? 'Select needs at least 2 options.' : undefined;

  const mutation = useMutation({
    mutationFn: () => {
      const current = {
        label,
        type,
        required,
        options: type === 'select' ? parsedOptions : null,
      };
      if (!question) {
        // REAL BUG FOUND AND FIXED: this used to always send `options: null` for every type
        // except Select - createCustomQuestionSchema's `options: z.array(z.string()).optional()`
        // accepts a missing key, never an explicit null, so saving a Text/Checkbox/Number
        // question always 400'd. There's no prior value on a CREATE, so it's only included when
        // actually relevant.
        const body: Partial<CustomQuestion> = {
          label: current.label,
          type: current.type,
          required: current.required,
        };
        if (current.options !== null) body.options = current.options;
        return api.customQuestions.create(eventId, body);
      }
      // FIXED (EDIT path - same documented known limitation as the other forms fixed this round,
      // `docs/FORM_SWEEP_RESULTS.md` "Known real gaps" #4): switching a question away from
      // `select` never cleared its stale `options` array server-side. `buildPatchPayload` diffs
      // against the question as loaded: unchanged fields are omitted, `options` cleared to blank
      // is sent as an explicit `null` (now accepted - `updateCustomQuestionSchema`'s `options` is
      // `.nullable().optional()`), a changed value is sent as-is.
      const original = {
        label: question.label,
        type: question.type,
        required: question.required,
        options: question.options ?? null,
      };
      return api.customQuestions.update(question.id, buildPatchPayload(original, current));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-questions', eventId] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={question ? 'Edit question' : 'New question'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!label.trim() || !!optionsError || mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Question label">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Dietary requirements?"
        />
      </FormField>
      <FormField label="Answer type">
        <Select value={type} onChange={(e) => setType(e.target.value as CustomQuestion['type'])}>
          <option value="text">Text</option>
          <option value="select">Select (dropdown)</option>
          <option value="checkbox">Checkbox</option>
          <option value="number">Number</option>
        </Select>
      </FormField>
      {type === 'select' && (
        <FormField label="Options" hint="Comma-separated, at least 2" error={optionsError}>
          <Input
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            placeholder="Vegetarian, Vegan, None"
          />
        </FormField>
      )}
      <Toggle checked={required} onChange={setRequired} label="Required" />
    </Modal>
  );
}
